<template>
  <!-- ==================== 기사 방문 캘린더 (ADMIN-only, Figma 4006:831) ==================== -->
  <div v-if="isAdmin" :class="['cal-panel', { 'cal-panel--mobile': isMobile }]">
    <!-- 툴바 -->
    <header class="cal-panel__toolbar">
      <div class="cal-panel__nav">
        <button type="button" class="cal-panel__nav-btn" :aria-label="t('dashboard.calendar.prev')" @click="goPrevMonth">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M10 4l-4 4 4 4" fill="none" stroke="currentColor" stroke-width="1.5" />
          </svg>
        </button>
        <span class="cal-panel__label">{{ monthLabel }}</span>
        <button type="button" class="cal-panel__nav-btn" :aria-label="t('dashboard.calendar.next')" @click="goNextMonth">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" />
          </svg>
        </button>
      </div>
      <div class="cal-panel__legend">
        <span class="cal-panel__legend-item">
          <span class="cal-panel__legend-dot cal-panel__legend-dot--scheduled" aria-hidden="true"></span>
          <span class="cal-panel__legend-label">{{ t('dashboard.calendar.legend.scheduled') }}</span>
        </span>
        <span class="cal-panel__legend-item">
          <span class="cal-panel__legend-dot cal-panel__legend-dot--completed" aria-hidden="true"></span>
          <span class="cal-panel__legend-label">{{ t('dashboard.calendar.legend.completed') }}</span>
        </span>
      </div>
      <button type="button" class="cal-panel__today-btn" @click="goToday">
        {{ t('dashboard.calendar.today') }}
      </button>
    </header>

    <!-- 요일 헤더 -->
    <div class="cal-panel__weekdays">
      <span
        v-for="(day, i) in weekdayLabels"
        :key="i"
        :class="[
          'cal-panel__weekday',
          { 'cal-panel__weekday--sun': i === 0, 'cal-panel__weekday--sat': i === 6 }
        ]"
      >
        {{ t(day) }}
      </span>
    </div>

    <!-- 월 그리드 -->
    <div class="cal-panel__grid">
      <button
        v-for="cell in cells"
        :key="cell.key"
        type="button"
        :class="[
          'cal-cell',
          { 'cal-cell--outside': cell.outsideMonth },
          { 'cal-cell--today': cell.isToday },
          { 'cal-cell--has-visits': cell.totalCount > 0 },
          { 'cal-cell--sun': cell.dayOfWeek === 0 },
          { 'cal-cell--sat': cell.dayOfWeek === 6 }
        ]"
        :disabled="cell.totalCount === 0"
        @click="openDay(cell)"
      >
        <span class="cal-cell__date">{{ cell.day }}</span>

        <!-- 신규: 인라인 이벤트 리스트 (백엔드 visits[] 배열 도착 시) -->
        <ul v-if="cell.visits.length > 0" class="cal-cell__events">
          <li
            v-for="v in cell.visits.slice(0, MAX_VISIBLE)"
            :key="v.requestId"
            :class="['cal-event', `cal-event--${(v.processStatus || 'visit_planned').toLowerCase()}`]"
            :title="formatEventTooltip(v)"
          >
            {{ formatEventLine(v) }}
          </li>
          <li
            v-if="cell.totalCount > MAX_VISIBLE"
            class="cal-event cal-event--more"
          >
            {{ t('dashboard.calendar.moreCount', { n: cell.totalCount - MAX_VISIBLE }) }}
          </li>
        </ul>

        <!-- Fallback: 백엔드가 visits[] 배포 전이면 카운트 뱃지로 graceful degrade -->
        <span v-else-if="cell.totalCount > 0" class="cal-cell__event">
          {{ t('dashboard.calendar.visitCount', { n: cell.totalCount }) }}
        </span>
      </button>
    </div>

    <!-- 일자 모달 -->
    <TechnicianCalendarDayModal
      v-model:show="modalOpen"
      :title="modalTitle"
      :visits="dayVisits"
      :loading="dayLoading"
      @initialize="onModalClose"
    />
  </div>
</template>

<script setup>
/* global defineProps */
import { ref, computed, onMounted, onActivated, watch } from 'vue'
import i18n from '@/locales/i18n'
import TechnicianCalendarDayModal from './TechnicianCalendarDayModal.vue'
import { useDashboardPermission } from '@/composables/useDashboardPermission'
import {
  getTechnicianCalendar,
  getTechnicianCalendarDay
} from '@/api/domain/dashboard'

const t = i18n.global.t

defineProps({
  isMobile: { type: Boolean, default: false }
})

const { isAdmin } = useDashboardPermission()

/** 셀당 인라인 표시 최대 건수 — 시안 4006:831 기준 (셀 minHeight 90px → 3건 가시) */
const MAX_VISIBLE = 3

const weekdayLabels = [
  'dashboard.calendar.weekday.sun',
  'dashboard.calendar.weekday.mon',
  'dashboard.calendar.weekday.tue',
  'dashboard.calendar.weekday.wed',
  'dashboard.calendar.weekday.thu',
  'dashboard.calendar.weekday.fri',
  'dashboard.calendar.weekday.sat'
]

/** 현재 표시 중인 월 — 매번 1일로 정규화 */
const cursor = ref(new Date())
const monthData = ref(null)

const modalOpen = ref(false)
const dayLoading = ref(false)
const dayVisits = ref([])
const selectedDate = ref('')

const monthLabel = computed(() =>
  t('dashboard.calendar.monthLabel', {
    year: cursor.value.getFullYear(),
    month: cursor.value.getMonth() + 1
  })
)

const modalTitle = computed(() =>
  selectedDate.value
    ? t('dashboard.calendar.modal.title', { date: selectedDate.value })
    : ''
)

function toYearMonth(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

function toDateStr(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * 7×6 그리드 셀 — outsideMonth 포함.
 * 각 셀은 totalCount(전체 건수) + visits[](상위 N건 요약)를 보유한다.
 * 백엔드 응답에 visits/totalCount가 부재하면 visitCount fallback (카운트 뱃지 graceful degrade).
 */
const cells = computed(() => {
  const year = cursor.value.getFullYear()
  const month = cursor.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const startOfGrid = new Date(year, month, 1 - firstDay.getDay())
  const today = toDateStr(new Date())
  const visitMap = Object.create(null)
  if (monthData.value && Array.isArray(monthData.value.days)) {
    for (const d of monthData.value.days) {
      visitMap[d.date] = {
        totalCount: Number(d.totalCount ?? d.visitCount) || 0,
        visits: Array.isArray(d.visits) ? d.visits : []
      }
    }
  }
  const out = []
  for (let i = 0; i < 42; i++) {
    const date = new Date(startOfGrid)
    date.setDate(startOfGrid.getDate() + i)
    const dateStr = toDateStr(date)
    const entry = visitMap[dateStr] || { totalCount: 0, visits: [] }
    out.push({
      key: dateStr,
      date,
      dateStr,
      day: date.getDate(),
      dayOfWeek: date.getDay(),
      outsideMonth: date.getMonth() !== month,
      isToday: dateStr === today,
      totalCount: entry.totalCount,
      visits: entry.visits
    })
  }
  return out
})

/**
 * 인라인 이벤트 라인 — '14:00 김수리 · 이디야 여의도점' 형식.
 * 누락 필드는 안전하게 폴백.
 */
function formatEventLine(visit) {
  const time = visit?.visitTime || ''
  const tech = visit?.technicianName || ''
  const partner = visit?.partnerName || visit?.customerName || ''
  const head = [time, tech].filter(Boolean).join(' ')
  return partner ? `${head} · ${partner}` : head
}

/** 호버 툴팁 — partner + customer 모두 노출 */
function formatEventTooltip(visit) {
  const parts = [
    visit?.visitTime,
    visit?.technicianName,
    visit?.partnerName,
    visit?.customerName
  ].filter(Boolean)
  return parts.join(' · ')
}

async function fetchMonth() {
  if (!isAdmin.value) return
  try {
    const yearMonth = toYearMonth(cursor.value)
    const res = await getTechnicianCalendar(yearMonth)
    monthData.value = res || null
  } catch (e) {
    if (typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
      window.__APP_LOG__(e)
    }
    monthData.value = null
  }
}

function goPrevMonth() {
  const next = new Date(cursor.value)
  next.setDate(1)
  next.setMonth(next.getMonth() - 1)
  cursor.value = next
}

function goNextMonth() {
  const next = new Date(cursor.value)
  next.setDate(1)
  next.setMonth(next.getMonth() + 1)
  cursor.value = next
}

function goToday() {
  cursor.value = new Date()
}

async function openDay(cell) {
  if (cell.totalCount === 0) return
  selectedDate.value = cell.dateStr
  modalOpen.value = true
  dayLoading.value = true
  dayVisits.value = []
  try {
    const res = await getTechnicianCalendarDay(cell.dateStr)
    dayVisits.value = res && Array.isArray(res.visits) ? res.visits : []
  } catch (e) {
    if (typeof window !== 'undefined' && typeof window.__APP_LOG__ === 'function') {
      window.__APP_LOG__(e)
    }
    dayVisits.value = []
  } finally {
    dayLoading.value = false
  }
}

function onModalClose() {
  selectedDate.value = ''
  dayVisits.value = []
}

watch(cursor, fetchMonth)

onMounted(() => {
  fetchMonth()
})

// SURINP-290: 상세 라우트에서 뒤로가기로 keep-alive 복귀 시 stale modalOpen=true가 남아
// 모달이 자동 재오픈되는 결함이 있어 activated 훅에서 명시적으로 리셋한다.
onActivated(() => {
  modalOpen.value = false
  selectedDate.value = ''
  dayVisits.value = []
})
</script>

<style scoped>
/* ========================================
   TechnicianCalendarPanel — Figma 4006:831
   인라인 이벤트 표시 (보라 단색) + +N건 더보기
   ======================================== */
.cal-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cal-panel__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.cal-panel__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cal-panel__nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  color: var(--color-gray-600, #424750);
}

.cal-panel__nav-btn:hover {
  border-color: var(--color-primary, #3B5998);
  color: var(--color-primary, #3B5998);
}

.cal-panel__nav-btn svg { width: 12px; height: 12px; }

.cal-panel__label {
  font-size: 14px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  min-width: 110px;
  text-align: center;
}

/* ===== Legend (2종: 방문예정 보라 / 완료 초록) ===== */
.cal-panel__legend {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.cal-panel__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cal-panel__legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.cal-panel__legend-dot--scheduled   { background: #8b5cf6; }
.cal-panel__legend-dot--completed   { background: #10b981; }

.cal-panel__legend-label {
  font-size: 11px;
  color: var(--color-gray-500, #5C616B);
  font-weight: 500;
}

.cal-panel__today-btn {
  padding: 4px 12px;
  border: 1px solid var(--color-gray-200, #B8BCC4);
  background: var(--color-white, #fff);
  color: var(--color-gray-600, #424750);
  border-radius: var(--radius-full, 9999px);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.cal-panel__today-btn:hover {
  border-color: var(--color-primary, #3B5998);
  color: var(--color-primary, #3B5998);
}

/* ===== Weekdays ===== */
.cal-panel__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  padding-bottom: 4px;
}

.cal-panel__weekday {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-gray-600, #424750);
  text-align: center;
  padding: 4px 0;
}

.cal-panel__weekday--sun { color: var(--color-danger, #C0392B); }
.cal-panel__weekday--sat { color: var(--color-info, #2C7BB6); }

/* ===== Grid ===== */
.cal-panel__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.cal-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 6px 6px 5px;
  min-height: 90px;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-gray-100, #E8EAED);
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  text-align: left;
  transition: border-color var(--transition-fast, 0.15s ease),
    background var(--transition-fast, 0.15s ease);
  align-items: stretch;
  overflow: hidden;
}

.cal-cell:not(:disabled):hover {
  border-color: #8b5cf6;
}

.cal-cell:disabled {
  cursor: default;
  background: var(--color-gray-50, #F3F4F6);
}

.cal-cell--outside .cal-cell__date {
  color: var(--color-gray-300, #A0A5AE);
}

.cal-cell--outside .cal-cell__events,
.cal-cell--outside .cal-cell__event {
  opacity: 0.5;
}

.cal-cell--today {
  border-color: var(--color-primary, #3B5998);
  background: rgba(59, 89, 152, 0.06);
}

.cal-cell--has-visits {
  background: var(--color-white, #fff);
}

.cal-cell--sun .cal-cell__date { color: var(--color-danger, #C0392B); }
.cal-cell--sat .cal-cell__date { color: var(--color-info, #2C7BB6); }

/* outside 셀 토/일 색상 override — 본달 빨강/파랑과 시각 분리, 회색 톤으로 통일 */
.cal-cell--outside.cal-cell--sun .cal-cell__date,
.cal-cell--outside.cal-cell--sat .cal-cell__date {
  color: var(--color-gray-300, #A0A5AE);
}

.cal-cell__date {
  font-size: 12px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  flex-shrink: 0;
}

/* ===== Inline Events (시안 보라 #8b5cf6 / bg #ede9fe) ===== */
.cal-cell__events {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-width: 0;
}

.cal-event {
  background: #ede9fe;
  color: #8b5cf6;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  padding: 1px 5px;
  border-radius: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

/* processStatus modifier — 백엔드 enum 매핑(소문자, SURINP-213에서 IN_PROGRESS 제거):
 *   VISIT_PLANNED → 보라(기본 .cal-event 색 그대로)
 *   COMPLETED     → 초록 (완료 안정 톤)
 * 알 수 없는 status는 cal-event 기본 보라로 graceful fallback. */
.cal-event--visit_planned {
  /* default — .cal-event 기본 보라 유지 */
}
.cal-event--completed {
  background: #d1fae5;
  color: #047857;
}

.cal-event--more {
  background: transparent;
  color: #94a3b8;
  font-weight: 500;
  padding: 0 4px;
}

/* Fallback 카운트 뱃지 (백엔드 visits[] 미배포 시) */
.cal-cell__event {
  font-size: 10px;
  color: var(--color-white, #fff);
  background: #8b5cf6;
  padding: 1px 5px;
  border-radius: var(--radius-sm, 3px);
  font-weight: 600;
  align-self: flex-end;
}

/* ===== Mobile (<768px) ===== */
@media (max-width: 767px) {
  .cal-cell {
    min-height: 64px;
    padding: 4px;
    gap: 2px;
  }
  .cal-cell__date { font-size: 11px; }
  .cal-event { font-size: 8.5px; padding: 1px 3px; line-height: 1.3; }
  .cal-event--more { font-size: 8px; }
  .cal-cell__event { font-size: 9px; padding: 1px 3px; }
  .cal-panel__label { font-size: 13px; min-width: 90px; }
  .cal-panel__legend-label { font-size: 10px; }
}
</style>
